import React, { useState } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import UsekoLogo from "@/components/common/UsekoLogo";

const menuGroups = (t) => [
  { label: t.nav_home, path: "/" },
  {
    label: t.nav_why_useko,
    items: [
      { label: t.what_useko_title || "USEKO Nedir?", path: "/neden-useko/usekonedir" },
      { label: t.why_useko_title || "Neden USEKO?", path: "/neden-useko/neden" },
    ],
  },
  { label: t.nav_president_message, path: "/baskanin-mesaji" },
  {
    label: t.nav_about,
    items: [
      { label: t.nav_about_us, path: "/kurumsal/hakkimizda" },
      { label: t.nav_mission, path: "/kurumsal/misyon-vizyon" },
      { label: t.nav_principles, path: "/kurumsal/ilkeler" },
      { label: t.nav_organization, path: "/kurumsal/organizasyon" },
    ],
  },
  {
    label: t.nav_activities,
    items: [
      { label: t.nav_area_merged_industry, path: "/faaliyet-alanlari/sanayi-teknoloji-savunma" },
      { label: t.act_social, path: "/faaliyet-alanlari/sosyal-kalkinma" },
      { label: t.nav_area_education_academic, path: "/faaliyet-alanlari/egitim-akademik" },
      { label: t.act_culture, path: "/faaliyet-alanlari/kulturel-diplomasi" },
      { label: t.nav_area_research_publishing, path: "/faaliyet-alanlari/arastirma-yayin" },
      { label: t.act_financial, path: "/faaliyet-alanlari/mali-ticari" },
    ],
  },
  {
    label: t.nav_programs,
    items: [
      { label: t.program_intl_cooperation, path: "/programlar/uluslararasi-is-birligi" },
      { label: t.program_trade_delegations, path: "/programlar/ticaret-heyetleri" },
      { label: t.program_academic_partnerships, path: "/programlar/akademik-is-birlikleri" },
      { label: t.program_youth, path: "/programlar/genclik" },
      { label: t.program_women_entrepreneurship, path: "/programlar/kadin-girisimciligi" },
      { label: t.program_volunteering, path: "/programlar/gonulluluk" },
      { label: t.program_leadership, path: "/programlar/liderlik" },
      { label: t.program_social_impact, path: "/programlar/sosyal-etki" },
    ],
  },
  {
    label: t.nav_news,
    items: [
      { label: t.nav_news, path: "/haberler" },
      { label: t.latest_announcements || "Duyurular", path: "/duyurular" },
      { label: t.nav_press_releases, path: "/basin-bultenleri" },
    ],
  },
  {
    label: t.nav_contact,
    items: [
      { label: t.nav_contact_info, path: "/iletisim" },
      { label: t.nav_contact_form, path: "/iletisim#form" },
      { label: t.nav_social_media, path: "/iletisim#social" },
    ],
  },
];

function DesktopMegaChairmanships() { return null; }

function DesktopDropdown({ label, items }) {
  return (
    <div className="relative group">
      <button className="flex items-center gap-1 px-2.5 py-2 text-[13px] font-medium text-[#102A43] hover:text-[#1F8A5B] transition-colors whitespace-nowrap" data-testid={`nav-group-${label}`}>
        {label} <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>
      <div className="absolute top-full start-0 pt-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200 z-50">
        <div className="min-w-[240px] bg-white border border-[#E5E7EB] rounded-xl shadow-xl p-2">
          {items.map((it) => (
            <Link
              key={it.path}
              to={it.path}
              className="block px-3 py-2 text-sm text-[#102A43] hover:bg-[#EAF7F0] hover:text-[#0F5132] rounded-lg transition-colors whitespace-nowrap"
              data-testid={`nav-link-${it.path}`}
            >
              {it.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function LanguageSwitcher({ compact = false }) {
  const { lang, setLang, languages } = useLanguage();
  return (
    <div className="flex items-center gap-0.5 border border-[#E5E7EB] rounded-full px-2 py-1" data-testid="lang-switcher">
      <Globe className="w-3.5 h-3.5 text-[#4B5563] me-1" />
      {languages.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full transition-colors ${lang === l.code ? "bg-[#EAF7F0] text-[#0F5132]" : "text-[#4B5563] hover:text-[#102A43]"}`}
          data-testid={`lang-btn-${l.code}`}
        >
          {l.name}
        </button>
      ))}
    </div>
  );
}

function ThemeToggle() {
  return null;
}

export default function Header() {
  const { t, lang } = useLanguage();
  const groups = menuGroups(t);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  return (
    <header className="glass-nav sticky top-0 z-40" data-testid="site-header">
      <div className="useko-container flex items-center justify-between gap-4 h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-link">
          <UsekoLogo className="h-11 md:h-12 w-auto"/>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-0.5 flex-1 justify-center" data-testid="desktop-nav">
          {groups.map((g) => {
            if (g.items) return <DesktopDropdown key={g.label} label={g.label} items={g.items} />;
            return (
              <NavLink
                key={g.path}
                to={g.path}
                end={g.path === "/"}
                className={({ isActive }) => `px-2.5 py-2 text-[13px] font-medium transition-colors whitespace-nowrap ${isActive ? "text-[#1F8A5B]" : "text-[#102A43] hover:text-[#1F8A5B]"}`}
                data-testid={`nav-link-${g.path}`}
              >
                {g.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Actions */}
        <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
          <ThemeToggle />
          <LanguageSwitcher />
        </div>

        {/* Mobile toggle */}
        <button
          className="xl:hidden p-2 -mr-2 text-[#102A43]"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="xl:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-drawer">
          <div className="useko-container py-4 max-h-[80vh] overflow-y-auto">
            <div className="mb-4 flex items-center gap-3"><LanguageSwitcher /><ThemeToggle /></div>
            {groups.map((g) => (
              <div key={g.label} className="border-b border-[#E5E7EB] py-2">
                {g.items ? (
                  <>
                    <button
                      onClick={() => setOpenGroup(openGroup === g.label ? null : g.label)}
                      className="w-full flex items-center justify-between py-2 text-sm font-medium text-[#102A43]"
                      data-testid={`mobile-group-${g.label}`}
                    >
                      {g.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${openGroup === g.label ? "rotate-180" : ""}`} />
                    </button>
                    {openGroup === g.label && (
                      <div className="ps-3 pb-2 space-y-1">
                        {g.items.map((it) => (
                          <Link
                            key={it.path}
                            to={it.path}
                            onClick={() => setMobileOpen(false)}
                            className="block py-1.5 text-sm text-[#4B5563] hover:text-[#1F8A5B]"
                            data-testid={`mobile-link-${it.path}`}
                          >
                            {it.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={g.path}
                    onClick={() => setMobileOpen(false)}
                    className="block py-2 text-sm font-medium text-[#102A43] hover:text-[#1F8A5B]"
                    data-testid={`mobile-link-${g.path}`}
                  >
                    {g.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
